import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn, queries, service, useAuthConfig } from "@theprelude/core";
import { Button, ToggleButton } from "@theprelude/ds";
import { Alert as AlertType } from "@theprelude/sdk";
import { forwardRef, useId, useMemo, useRef, useState } from "react";
import { useOverlay } from "@/hooks/use-overlay";
import { Alert } from "./alert";

export function AlertsList({
  alerts,
  onClick,
  className,
}: {
  alerts: AlertType[];
  onClick?: () => void;
  className?: string;
}) {
  const overlay = useOverlay();
  const containerRef = useRef(null);

  const [filterUnread, setFilterUnread] = useState(false);
  const toggleFilter = () => setFilterUnread((prev) => !prev);

  const filteredAlerts = useMemo(
    () => (filterUnread ? alerts.filter((a: AlertType) => !a.read_at) : alerts),
    [alerts, filterUnread],
  );

  const rowVirtualizer = useVirtualizer({
    count: filteredAlerts.length,
    estimateSize: () => 38,
    getScrollElement: () => containerRef.current,
    measureElement:
      typeof window !== "undefined" &&
      navigator.userAgent?.indexOf("Firefox") === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });

  const authConfig = useAuthConfig();
  const queryClient = useQueryClient();

  const { mutate: markAlertsRead } = useMutation({
    mutationFn: (ids: string[] | undefined) =>
      service(authConfig).detect.markAlertsRead({ ids }),
    onMutate: async (ids) => {
      const { queryKey } = queries.detect.getAlerts(authConfig);
      await queryClient.cancelQueries({ queryKey });

      const alerts = queryClient.getQueryData<AlertType[]>(queryKey);
      if (alerts) {
        const readAlerts: AlertType[] = alerts.map((alert) => ({
          ...alert,
          read_at:
            ids === undefined || ids.includes(alert.id)
              ? new Date().toString()
              : alert.read_at,
        }));
        queryClient.setQueryData<AlertType[]>(queryKey, readAlerts);
      }

      return { prevAlerts: alerts };
    },
    onError: (_err, _vars, context) => {
      const { queryKey } = queries.detect.getAlerts(authConfig);
      if (context) {
        queryClient.setQueryData(queryKey, context.prevAlerts);
      }
    },
  });

  return (
    <div className={cn("flex flex-col gap-4 overflow-hidden", className)}>
      <AlertsHeader
        isFiltering={filterUnread}
        toggleFilter={toggleFilter}
        onMarkAlertsRead={() => markAlertsRead(undefined)}
      />
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto border border-stroke-table"
      >
        {filteredAlerts.length === 0 ? (
          <div className="p-auto p-8 text-center text-secondary-foreground">
            No alerts
          </div>
        ) : (
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualItem) => {
              const alert = filteredAlerts[virtualItem.index] as AlertType;
              return (
                <AlertItem
                  key={virtualItem.key}
                  ref={rowVirtualizer.measureElement}
                  index={virtualItem.index}
                  offset={virtualItem.start}
                  alert={alert}
                  onClick={async () => {
                    await overlay.open("alert", { id: alert.id });
                    onClick?.();
                    if (!alert.read_at) {
                      markAlertsRead([alert.id]);
                    }
                  }}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const AlertsHeader = ({
  isFiltering,
  toggleFilter,
  onMarkAlertsRead,
}: {
  isFiltering: boolean;
  toggleFilter: () => void;
  onMarkAlertsRead: () => void;
}) => {
  const id = useId();

  return (
    <div className="flex items-center justify-between gap-4">
      <Button
        intent="text-link"
        onClick={onMarkAlertsRead}
        className="p-0 no-underline hover:underline"
      >
        Mark all as read
      </Button>
      <div className="flex items-center gap-2">
        <label htmlFor={id} className="cursor-pointer">
          Only show unread
        </label>
        <ToggleButton id={id} checked={isFiltering} onChange={toggleFilter} />
      </div>
    </div>
  );
};

const AlertItem = forwardRef<
  HTMLDivElement,
  { index: number; alert: AlertType; offset: number; onClick: () => void }
>(function AlertItem({ index, alert, offset, onClick }, ref) {
  return (
    <Alert
      ref={ref}
      alert={alert}
      data-index={index}
      onClick={onClick}
      className={cn(
        "w-full cursor-pointer border-t border-stroke-table",
        index === 0 && "border-transparent",
      )}
      style={{
        position: "absolute",
        transform: `translateY(${offset}px)`,
      }}
    />
  );
});
